<template>
  <div class="RunWorkflow mt-5">
    <v-row dense align="center" class="mb-2">
      <v-col cols="auto" class="mr-auto">
        <p class="text-subtitle-1 mb-0">Expected Input Fields</p>
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-if="showAddParamBtn"
          @click="addParam()"
          text
          color="primary"
          dense
        >
          + Add
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="showDialog" width="700">
      <v-card>
        <v-card-title class="InputSettings__modal-title">
          JavaScript code editor
        </v-card-title>

        <v-card-text class="InputSettings__modal-content">
          <CodeEditor
            v-if="showDialog"
            :value="settings.script"
            @input="(val) => onUpdate(val, 'settings.script')"
            language="javascript"
            class="mb-3"
            :runScript="runTestInput"
            :placeholder="placeholder"
            :suggestions="[]"
          />
          <p v-if="error" class="error--text">
            {{ error }}
          </p>
          <div v-if="testScriptOutput">
            <p
              v-if="!isNullOrUndefined(testScriptOutput)"
              class="text-subtitle-1 mb-3 mt-3"
            >
              Test Result:
            </p>
            <json-viewer
              v-if="!isNullOrUndefined(testScriptOutput) && !testingScript"
              :value="testScriptOutput"
              :expand-depth="0"
              boxed
              sort
              copyable
            ></json-viewer>
          </div>
          <div v-if="testScriptOutput">
            <p
              v-if="!isNullOrUndefined(testScriptOutput)"
              class="text-subtitle-1 mb-3 mt-3"
            >
              Sample Input:
            </p>
            <json-viewer
              v-if="!isNullOrUndefined(testScriptOutput) && !testingScript"
              :value="settings.init_input"
              :expand-depth="0"
              boxed
              sort
              copyable
            ></json-viewer>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="justify-end py-3 px-4">
          <v-btn color="primary" text @click="showDialog = false">Done</v-btn>
          <v-btn
            color="primary"
            :loading="testingScript"
            @click="runTestInput()"
          >
            Run Input Script
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div
      class="RunWorkflow__param-row mb-6"
      v-for="(param, i) in settings.params"
      :key="i"
    >
      <v-text-field
        class="RunWorkflow__param-row--field mr-2"
        outlined
        dense
        label="Name"
        :value="param.name"
        @input="
          (val) => {
            onUpdate(val, `settings.params[${i}].name`);
            onUpdate('Object.' + val, `settings.params[${i}].value`);
          }
        "
      />
      <v-text-field
        class="RunWorkflow__param-row--field mr-2"
        outlined
        dense
        label="Value"
        :value="'Object.' + param.name"
        disabled
      />
      <v-btn
        class="RunWorkflow__param-row--icon-btn"
        icon
        @click="deleteParam(i)"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </div>

    <v-btn
      class="my-5"
      color="primary"
      :loading="testingScript"
      @click="showDialog = true"
    >
      Generate Sample Input and Model
    </v-btn>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { getModel } from '@/util/actionsModels';
import JsonViewer from 'vue-json-viewer';
import CodeEditor from '@/components/code-editor/code-editor.vue';
import { helpers } from '@/mixins/helpers';

const {
  mapGetters: workflowDetailsGetters,
  mapActions: workflowDetailsActions,
} = createNamespacedHelpers('workflows/details');

export default {
  name: 'workflow-input-settings',
  components: {
    CodeEditor,
    JsonViewer,
  },
  props: {
    availableInputFields: [],
  },
  mixins: [helpers],
  data() {
    return {
      testingScript: false,
      testOutput: null,
      showDialog: false,
      selectedWorkflow: {},
      newInputScript: '',
      testScriptOutput: '',
      placeholder: `
      // Example:
      function myFunction() {
        // return sample input payload
        // must be an object, containing the key(s) in the input fields
        return {
          data: [
            { name: 'John Doe', age: 30 },
            { name: 'Jane Doe', age: 25 },
          ];
        };
      }
      myFunction()`,
      error: '',
    };
  },
  computed: {
    ...workflowDetailsGetters({
      selectedNode: 'SELECTED_NODE',
    }),
    settings() {
      return this.selectedNode.settings;
    },
  },
  methods: {
    ...workflowDetailsActions(['executeSingleAction', 'getWorkflowTriggerIO']),
    async runTestInput() {
      try {
        this.error = '';
        this.testingScript = true;

        const options = {
          creds: null,
          data: {
            script: this.settings.script,
          },
          action: 'execute_javascript',
          params: [],
        };
        const data = await this.executeSingleAction(options);
        if (!data || !data.success) {
          throw Error(data && data.message);
        }

        const output = data.data ?? data.result ?? 'no output';
        this.testScriptOutput = output;

        const payload = this.extractMatchingKeys(output);
        const output_type = getModel(payload);
        this.onUpdate(output_type, 'output_type');
        this.onUpdate(payload, 'settings.init_input');
      } catch (e) {
        this.error = e.data ? e.data.message : e;
      } finally {
        this.testingScript = false;
      }
    },
    onUpdate(value, path) {
      this.$emit('update', value, path);
    },
    addParam() {
      let settings = cloneDeep(this.settings);
      if (!settings.params) {
        settings.params = [];
      }

      settings.params.push({
        name: '',
        value: '',
      });
      this.onUpdate(settings, 'settings');
    },
    deleteParam(index) {
      const settings = cloneDeep(this.settings);
      settings.params.splice(index, 1);
      if (!settings.params.length) delete settings.params;

      this.onUpdate(settings, 'settings');
    },
    showAddParamBtn() {
      const params = this.settings.params;
      return (
        !params ||
        !params[0] ||
        (params[params.length - 1].name && params[params.length - 1].value)
      );
    },
    extractMatchingKeys(obj) {
      const keys = this.settings.params.map((param) => param.name);
      const result = {};
      keys.forEach((key) => {
        result[key] = obj[key];
      });
      return result;
    },
  },
};
</script>

<style scoped lang="scss">
@import './run-workflow/run-workflow';
</style>
